import { useCallback, useEffect, useState } from 'react'
import { preisText, STEUERHINWEIS } from '@raporto/shared'
import { OfficeHead } from '../ui/OfficeShell.js'
import { notify, notifyError } from '../ui/Toasts.js'
import {
  api,
  type AboStand,
  type Abointervall,
  type Ausstieg,
  type Kundenhandlung
} from '../net/apiClient.js'
import { fehlerText } from '../net/errors.js'
import { uuidv7 } from '../net/ids.js'

/**
 * «Mein Raporto» – das eigene Abo, aus der Sicht des Betriebs.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Wofür dieser Bildschirm da ist
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Bis hierher konnte ein Betrieb sein Paket nicht sehen und nicht wechseln.
 * Beides ging nur über die Control Plane, also über einen Anruf bei uns. Das
 * ist für einen Supportfall richtig und als Geschäftsprozess falsch: Ein
 * Anbieter, bei dem jeder Wechsel über einen Menschen läuft, wächst so
 * schnell wie dieser Mensch.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Was hier NICHT steht
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Kein Auswahlfeld mit BESTAND, AKTIV, GEKUENDIGT. Das sind unsere
 * Zustandsnamen und nicht die Sprache eines Handwerkbetriebs. Der Kunde tut
 * etwas – Paket wählen, Abo starten, kündigen –, und der Zustand folgt daraus.
 * Welche Handlung gerade möglich ist, sagt der Server in `handlungen`; eine
 * zweite Tabelle im Browser gäbe es nur, damit sie eines Tages abweicht.
 *
 * Und keine Zahlungsbestätigung. Es gibt kein Abrechnungssystem. «Abo
 * gestartet» heisst «vereinbart», und genau das steht auch da.
 */

const BALKEN_WARN = 80

/**
 * Der Zahlungsstand in Kundensprache.
 *
 * Dieselbe Regel wie beim Vertragszustand: Der Kunde liest nie unsere
 * Zustandsnamen. «past_due» ist ein Wort aus einer Schnittstelle und kein
 * Satz, den man einem Handwerker hinstellt.
 */
const ZAHLUNGSTEXT: Record<AboStand['zahlung']['status'], string> = {
  kein_abo: 'Noch kein Abo gebucht.',
  checkout_offen: 'Deine Buchung ist unterwegs – sobald die Zahlung bestätigt ist, gilt dein Paket.',
  aktiv: 'Dein Abo läuft und ist bezahlt.',
  zahlung_offen: 'Eine Zahlung ist offen. Schau bitte kurz unter Zahlungsmittel und Rechnungen nach.',
  beendet: 'Dieses Abo ist beendet.'
}

/**
 * Der Zahlungsstand – und zwar der, der wirklich gilt.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Warum die Tabelle oben nicht mehr genügt
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `status: 'aktiv'` fasst zwei Lagen zusammen, die Stripe getrennt führt:
 * `active` – belastet – und `trialing` – Zahlungsmittel hinterlegt, belastet
 * wurde nichts. Für die Berechtigung ist das Zusammenfassen richtig: in
 * beiden Fällen darf gearbeitet werden. Für den Satz auf dem Bildschirm war
 * es falsch. In der Testphase stand dort «Dein Abo läuft und ist bezahlt»,
 * und bezahlt war nichts.
 *
 * Dazu kam die Kündigung. Wer während der Testphase kündigte, las drei Sätze
 * nebeneinander: «Gekündigt», «Dein Abo läuft und ist bezahlt» und – aus dem
 * Band – «An diesem Tag buchen wir ab». Von den dreien stimmte einer.
 *
 * Vier Lagen, vier Sätze, eine Quelle:
 *
 *   Testphase, gekündigt     Es endet, es kommt nichts, nichts zu tun.
 *   Testphase, läuft         Noch nichts belastet – und wann es so weit ist.
 *   Bezahlt, gekündigt       Bezahlt bis zum Ende, danach ist Schluss.
 *   Bezahlt, läuft           Der alte Satz, und hier stimmt er.
 */
function zahlungstext(z: AboStand['zahlung'], bis: (w: string | null) => string): string {
  if (z.status !== 'aktiv') return ZAHLUNGSTEXT[z.status]

  const ende = z.periodeBis === null ? null : bis(z.periodeBis)

  if (z.inTestphase) {
    return z.kuendigungZumEnde
      ? ende === null
        ? 'Deine Testphase läuft noch. Du hast gekündigt – belastet wird nichts.'
        : `Deine Testphase läuft bis zum ${ende}. Du hast gekündigt; an diesem Tag endet dein Abo, belastet wird nichts.`
      : ende === null
        ? 'Deine Testphase läuft. Belastet wurde bisher nichts.'
        : `Deine Testphase läuft bis zum ${ende}. Belastet wurde bisher nichts.`
  }

  if (z.kuendigungZumEnde) {
    return ende === null
      ? 'Dein Abo ist bezahlt und läuft bis zum Ende der laufenden Periode. Danach wird es nicht verlängert.'
      : `Dein Abo ist bezahlt und läuft bis zum ${ende}. Danach wird es nicht verlängert.`
  }

  return ZAHLUNGSTEXT.aktiv
}

function Balken({ prozent }: { prozent: number }): JSX.Element {
  const stufe = prozent >= 100 ? 'alert' : prozent >= BALKEN_WARN ? 'warn' : 'ok'
  return (
    <span className="mr-balken" data-stufe={stufe} aria-hidden="true">
      <i style={{ width: `${Math.min(100, Math.max(2, prozent))}%` }} />
    </span>
  )
}

function datum(w: string | null): string {
  if (!w) return '—'
  const d = new Date(w.length === 10 ? `${w}T00:00:00Z` : w)
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export function MeinRaporto({
  startPaket = null,
  aufStartPaketErledigt
}: {
  /**
   * Das bei der Bestellung gewählte Paket – nur beim ersten Aufruf gesetzt.
   *
   * Damit hat dieser Bildschirm zwei Gesichter: der gewöhnliche Abobereich,
   * und der letzte Schritt einer Einrichtung. Der Unterschied ist genau ein
   * Band zuoberst; die Karten darunter bleiben dieselben.
   */
  startPaket?: string | null
  aufStartPaketErledigt?: () => void
} = {}): JSX.Element {
  const [stand, setStand] = useState<AboStand | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState<string | null>(null)
  const [wahl, setWahl] = useState<string | null>(null)
  const [kuendigen, setKuendigen] = useState(false)
  const [kgrund, setKgrund] = useState('')
  /*
    Monat oder Jahr – eine Entscheidung fuer die ganze Seite und nicht je
    Karte. Wer zwischen drei Paketen vergleicht, vergleicht sie beim selben
    Zahlungsrhythmus; ein Schalter pro Karte laedt zum Vertippen ein.
  */
  const [intervall, setIntervall] = useState<Abointervall>('monat')

  const laden = useCallback(async () => {
    try {
      setStand(await api.abo())
      setFehler(null)
    } catch (e) {
      setFehler(fehlerText(e))
    }
  }, [])

  useEffect(() => {
    void laden()
  }, [laden])

  /*
    Eine Handlung, ein Wartezustand, eine Rückmeldung – und danach neu laden.

    Dieselbe Mechanik wie in der Control Plane, und aus demselben Grund: Eine
    Handlung ohne sichtbaren Abschluss ist eine Handlung, die der Benutzer ein
    zweites Mal auslöst.
  */
  async function handeln(schluessel: string, was: () => Promise<string>): Promise<void> {
    if (laeuft) return
    setLaeuft(schluessel)
    try {
      notify.ok(await was())
      await laden()
      setWahl(null)
      setKuendigen(false)
      setKgrund('')
    } catch (e) {
      notifyError(e)
    } finally {
      setLaeuft(null)
    }
  }

  const wartet = (k: string): boolean => laeuft === k

  /**
   * Zu Stripe gehen.
   *
   * Kein `handeln()`: Dort wird nach dem Erfolg neu geladen und eine Meldung
   * gezeigt – hier verlaesst der Benutzer die Seite. Eine Erfolgsmeldung, die
   * niemand mehr sieht, waere nur eine Verzoegerung.
   */
  async function zuStripe(schluessel: string, hol: () => Promise<{ url: string }>): Promise<void> {
    if (laeuft) return
    setLaeuft(schluessel)
    try {
      const { url } = await hol()
      window.location.assign(url)
    } catch (e) {
      notifyError(e)
      setLaeuft(null)
    }
  }
  const darf = (h: Kundenhandlung): boolean =>
    stand !== null && stand.selbstbedienung && stand.handlungen.includes(h)

  /**
   * Im Lesemodus bleibt der Weg in die Kasse offen.
   *
   * `handlungen` ist für einen stillgelegten Betrieb leer, und das muss so
   * sein: Diese Liste entscheidet auch, von welchem Zustand aus der Webhook
   * einen Vertrag beginnen darf – ein stillgelegter gehört nicht dazu, er
   * kommt nur über eine bezahlte Rechnung zurück (`services/abo.ts`).
   *
   * Für die Oberfläche ist das zu streng: Wer im Lesemodus steht, soll genau
   * eines tun können – bezahlen. Der Server lässt ihn: `/abo` ist im
   * Lesemodus offen, und `checkoutStarten()` weist nur noch GELÖSCHT ab.
   */
  const imLesemodus = stand?.ausstieg?.phase === 'lesemodus'
  const darfBuchen = (h: Kundenhandlung): boolean =>
    darf(h) || (imLesemodus && stand !== null && stand.selbstbedienung)

  if (fehler) {
    return (
      <>
        <OfficeHead titel="Mein Raporto" />
        <p className="o-warn-text">{fehler}</p>
      </>
    )
  }
  if (!stand) {
    return (
      <>
        <OfficeHead titel="Mein Raporto" />
        <p className="o-soft">Einen Moment …</p>
      </>
    )
  }

  const z = stand.zahlung
  const n = stand.nutzung
  const startPaketName =
    stand.pakete.find((p) => p.id === startPaket)?.name ?? 'dein Paket'
  const benutzerProzent =
    n.benutzerGrenze && n.benutzerGrenze > 0 ? (n.benutzer / n.benutzerGrenze) * 100 : 0

  return (
    <div id="mein-raporto">
      <OfficeHead titel="Mein Raporto" unter="Abo, Paket und Nutzung deines Betriebs" />

      {/*
        Der letzte Schritt der Einrichtung.

        Produktionsbefund: Nach «Betrieb einrichten» kam nie eine Zahlung. Der
        Betrieb stand fertig da, das Paket war gewählt, und niemand hatte je
        nach Geld gefragt. Dieses Band ist der fehlende Schritt – und es steht
        nur da, solange er offen ist: kein Abo, Stripe eingerichtet, und ein
        Paket aus der Bestellung im Gepäck.
      */}
      {startPaket !== null && z.eingerichtet && z.status !== 'aktiv' && (
        <section className="o-card" id="mr-onboarding">
          <div className="o-pad">
            <h3>Noch ein Schritt: dein Abo</h3>
            <p className="o-soft">
              Dein Betrieb ist eingerichtet und du bist angemeldet. Damit {startPaketName} gilt,
              fehlt nur noch die Zahlung. Preise exkl. MWST.
            </p>
            <div className="mr-aktionen">
              <button
                className="o-btn o-btn-primary"
                id="onb-zur-zahlung"
                disabled={laeuft !== null}
                onClick={() =>
                  void zuStripe('onboarding', () =>
                    api.aboCheckout(startPaket, intervall, uuidv7())
                  )
                }
              >
                {wartet('onboarding') ? 'Einen Moment …' : `Weiter zur Zahlung`}
              </button>
              <button
                className="o-btn o-btn-ghost"
                id="onb-spaeter"
                onClick={() => aufStartPaketErledigt?.()}
              >
                Später
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ---------- Vertrag und Ausstieg ---------- */}
      {stand.ausstieg !== null && (
        /*
          Der eigene Bereich für den Ausstieg – und er steht ÜBER «Abo &
          Paket».

          Wer hier landet, weil im Band etwas stand, sucht zwei Daten und
          einen Knopf, nicht die Paketliste. Die Reihenfolge auf dem
          Bildschirm ist die Reihenfolge der Dringlichkeit.

          Das geplante Löschdatum erscheint nur, wenn der Server es
          herausgibt. Solange der sichere Purge fehlt, steht es auf `null`,
          und dann steht hier nichts dazu – auch kein Ersatztext.
        */
        <section className="o-card" id="mr-ausstieg" data-grund={stand.ausstieg.grund}>
          <div className="o-card-head">
            <h3>{ausstiegstitel(stand.ausstieg.grund)}</h3>
            <span
              className={
                stand.ausstieg.phase === 'laeuft'
                  ? 'o-badge o-badge-neutral'
                  : 'o-badge o-badge-warn'
              }
              id="mr-ausstieg-lage"
            >
              {lagetext(stand.ausstieg.phase)}
            </span>
          </div>
          <div className="o-pad">
            <dl className="o-werte">
              <div>
                <dt>
                  {stand.ausstieg.abbuchungAm !== null
                    ? 'Abbuchung am'
                    : stand.ausstieg.grund === 'test_abgelaufen'
                      ? 'Testphase bis'
                      : 'Bezahlt bis'}
                </dt>
                <dd id="mr-ausstieg-ende">{datum(stand.ausstieg.endeAm)}</dd>
              </div>
              {/*
                Sperrdatum und Resttage NUR, wenn der Server sie herausgibt.

                Bei einer offenen Zahlung mit abgeschalteter Sperre tut er das
                nicht – und dann steht hier nichts davon. Eine Frist auf dem
                Bildschirm, die niemand einhält, ist eine Ankündigung, die
                beim zweiten Mal keiner mehr glaubt.
              */}
              {stand.ausstieg.sperreAm !== null && (
                <div>
                  <dt>Lesemodus ab</dt>
                  <dd id="mr-ausstieg-sperre">{datum(stand.ausstieg.sperreAm)}</dd>
                </div>
              )}
              {/*
                Die Resttage der LAUFENDEN Phase – dieselbe Zahl wie im Band.

                Vorher stand hier immer `tageBisSperre`, auch während der
                Testphase: Daneben stand das Testende, und die beiden Zahlen
                gehörten zu verschiedenen Tagen.
              */}
              {(() => {
                const tage =
                  stand.ausstieg.phase === 'laeuft'
                    ? stand.ausstieg.tageBisEnde
                    : stand.ausstieg.phase === 'nachfrist'
                      ? stand.ausstieg.tageBisSperre
                      : stand.ausstieg.phase === 'lesemodus'
                        ? stand.ausstieg.tageBisLoeschung
                        : null
                if (tage === null) return null
                const offen = Math.max(tage, 0)
                return (
                  <div>
                    <dt>Verbleibend</dt>
                    <dd id="mr-ausstieg-tage">{offen === 1 ? '1 Tag' : `${offen} Tage`}</dd>
                  </div>
                )
              })()}
              {stand.ausstieg.loeschungVorgesehenAm !== null && (
                <div>
                  <dt>Löschung vorgesehen</dt>
                  <dd id="mr-ausstieg-loeschung">
                    {datum(stand.ausstieg.loeschungVorgesehenAm)}
                  </dd>
                </div>
              )}
            </dl>
            <p className="o-soft" id="mr-ausstieg-satz">
              {ausstiegssatz(stand.ausstieg)}
            </p>
            <div className="o-aktionen">
              {stand.ausstieg.phase !== 'geloescht' && (
                <a className="o-btn" id="mr-ausstieg-export" href="/exports/zip">
                  Daten exportieren
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ---------- Abo & Paket ---------- */}
      <section className="o-card" id="mr-abo">
        <div className="o-card-head">
          <h3>Abo &amp; Paket</h3>
          <span className="o-badge o-badge-neutral" id="mr-vertragstext">
            {stand.vertrag.text}
          </span>
        </div>
        <div className="o-pad">
          <dl className="o-werte">
            <div>
              <dt>Paket</dt>
              <dd id="mr-paketname">
                {stand.paket.name ?? 'keines gewählt'}
                {stand.paket.preisRp !== null && (
                  <span className="o-soft">
                    {' '}
                    · CHF {preisText(stand.paket.preisRp)}/Monat {STEUERHINWEIS}
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt>Abo seit</dt>
              <dd>{datum(stand.vertrag.beginn)}</dd>
            </div>
            {stand.vertrag.wirksamPer && (
              <div>
                <dt>Läuft bis</dt>
                <dd id="mr-wirksam">{datum(stand.vertrag.wirksamPer)}</dd>
              </div>
            )}
          </dl>
          <p className="o-soft">{stand.vertrag.erklaerung}</p>

          {/*
            Der ehrliche Satz über die Abrechnung.

            Er steht nicht im Kleingedruckten, sondern dort, wo jemand nach dem
            Preis schaut. Wer «Abo gestartet» liest und nichts weiter, nimmt an,
            dass abgebucht wird – und ruft an, wenn die erste Rechnung ausbleibt.
          */}
          {/*
            Der ehrliche Satz bleibt stehen, solange nicht wirklich verrechnet
            wird. Sobald Stripe eingerichtet ist UND ein Abo laeuft, steht hier
            der Zahlungsstand statt des Hinweises – nicht beides, sonst
            widersprechen sie sich.
          */}
          {z.eingerichtet && z.status !== 'kein_abo' ? (
            <p className="o-band" id="mr-zahlung">
              {zahlungstext(z, datum)}
              {z.periodeBis !== null && (
                <>
                  {' '}
                  {z.kuendigungZumEnde
                    ? `Dein Abo endet am ${datum(z.periodeBis)}.`
                    : `Naechste Verrechnung am ${datum(z.periodeBis)}.`}
                </>
              )}
              {z.bezahltesPaket !== null && z.intervall !== null && (
                <> Bezahlt: {z.intervall === 'jahr' ? 'Jahresabo' : 'Monatsabo'}.</>
              )}
            </p>
          ) : (
            <p className="o-band" id="mr-abrechnung">
              {stand.abrechnung.hinweis}
            </p>
          )}

          {z.portalMoeglich && (
            <div className="mr-aktionen">
              {/*
                Zahlungsmittel, Rechnungen, Kuendigung – alles bei Stripe. Was
                dort NICHT geht, ist der Paketwechsel: Der muss durch unsere
                Hindernisse, und die kennt Stripe nicht.
              */}
              <button
                className="o-btn o-btn-ghost"
                id="mr-btn-portal"
                disabled={laeuft !== null}
                onClick={() => void zuStripe('portal', () => api.aboPortal(uuidv7()))}
              >
                {wartet('portal') ? 'Wird geöffnet …' : 'Zahlungsmittel und Rechnungen'}
              </button>
            </div>
          )}

          <div className="mr-aktionen">
            {darf('abo_starten') && (
              <button
                className="o-btn o-btn-primary"
                id="mr-btn-starten"
                disabled={laeuft !== null}
                onClick={() =>
                  void handeln('starten', async () => {
                    await api.aboStarten(uuidv7())
                    return 'Dein Abo ist gestartet. Das Paket gilt ab heute.'
                  })
                }
              >
                {wartet('starten') ? 'Wird gestartet …' : 'Abo starten'}
              </button>
            )}
            {darf('kuendigung_zurueck') && (
              <button
                className="o-btn o-btn-primary"
                id="mr-btn-zurueck"
                disabled={laeuft !== null}
                onClick={() =>
                  void handeln('zurueck', async () => {
                    await api.aboKuendigungZurueck(uuidv7())
                    return 'Deine Kündigung ist zurückgenommen. Dein Abo läuft unverändert weiter.'
                  })
                }
              >
                {wartet('zurueck') ? 'Wird zurückgenommen …' : 'Kündigung zurücknehmen'}
              </button>
            )}
            {darf('kuendigen') && (
              <button
                className="o-btn o-btn-ghost"
                id="mr-btn-kuendigen"
                disabled={laeuft !== null}
                onClick={() => setKuendigen(!kuendigen)}
              >
                Abo kündigen
              </button>
            )}
          </div>

          {kuendigen && (
            <div className="mr-form" id="mr-form-kuendigen">
              <p>
                Deine Kündigung wird auf das Ende der laufenden Abrechnungsperiode wirksam. Bis
                dahin arbeitest du normal weiter, es geht nichts verloren, und du kannst sie
                jederzeit zurücknehmen.
              </p>
              <label htmlFor="mr-kgrund">Magst du uns sagen, warum? (freiwillig)</label>
              <input
                id="mr-kgrund"
                value={kgrund}
                onChange={(e) => setKgrund(e.target.value)}
                autoComplete="off"
              />
              <div className="mr-aktionen">
                <button className="o-btn o-btn-ghost" onClick={() => setKuendigen(false)}>
                  Abbrechen
                </button>
                <button
                  className="o-btn o-btn-alert"
                  id="mr-btn-kuendigen-bestaetigen"
                  disabled={laeuft !== null}
                  onClick={() =>
                    void handeln('kuendigen', async () => {
                      await api.aboKuendigen(kgrund.trim(), uuidv7())
                      return 'Deine Kündigung ist erfasst. Bis zum Ende der laufenden Periode kannst du sie jederzeit zurücknehmen.'
                    })
                  }
                >
                  {wartet('kuendigen') ? 'Wird erfasst …' : 'Kündigung bestätigen'}
                </button>
              </div>
            </div>
          )}

          {!stand.selbstbedienung && (
            <p className="o-soft" id="mr-nur-lesen">
              Paket und Abo stellen wir im Moment für dich um – melde dich kurz, dann ist es
              erledigt.
            </p>
          )}
        </div>
      </section>

      {/* ---------- Nutzung ---------- */}
      <section className="o-card" id="mr-nutzung">
        <div className="o-card-head">
          <h3>Nutzung</h3>
        </div>
        <div className="o-pad">
          <div className="mr-nutzungszeile">
            <div>
              <b id="mr-benutzer">
                {n.benutzer} von {n.benutzerGrenze ?? '—'} Benutzern
              </b>
              <Balken prozent={benutzerProzent} />
            </div>
            <div>
              <b id="mr-speicher">
                {n.speicher.gbBelegt} von {n.speicher.gbGrenze} GB · {n.speicher.prozent} % belegt
              </b>
              <Balken prozent={n.speicher.prozent} />
            </div>
          </div>
          <p className="o-soft">
            Gezählt werden angemeldete Personen sowie Fotos, Beleg-PDF und dein Logo. Deaktivierte
            Zugänge belegen keinen Platz.
          </p>
        </div>
      </section>

      {/* ---------- Paketkarten ---------- */}
      <section className="o-card" id="mr-pakete">
        <div className="o-card-head">
          <h3>Pakete</h3>
          {z.eingerichtet && (
            <div className="mr-intervall" role="group" aria-label="Zahlungsrhythmus">
              <button
                type="button"
                id="mr-intervall-monat"
                className={intervall === 'monat' ? 'active' : undefined}
                aria-pressed={intervall === 'monat'}
                onClick={() => setIntervall('monat')}
              >
                Monatlich
              </button>
              <button
                type="button"
                id="mr-intervall-jahr"
                className={intervall === 'jahr' ? 'active' : undefined}
                aria-pressed={intervall === 'jahr'}
                onClick={() => setIntervall('jahr')}
              >
                Jaehrlich
              </button>
            </div>
          )}
        </div>
        <div className="o-pad">
          <div className="mr-karten">
            {stand.pakete.map((p) => (
              <div
                key={p.id}
                className="mr-karte"
                data-aktuell={p.aktuell ? 'ja' : 'nein'}
                id={`mr-paket-${p.id}`}
              >
                <div className="mr-karte-kopf">
                  <b>{p.name}</b>
                  {p.aktuell && (
                    <span className="o-badge o-badge-ok" id={`mr-aktuell-${p.id}`}>
                      Dein Paket
                    </span>
                  )}
                </div>
                <div className="mr-preis">
                  CHF {preisText(p.preisRp)}
                  <span className="o-soft"> /Monat {STEUERHINWEIS}</span>
                </div>
                <div className="o-soft">{p.fuerWen}</div>
                <ul className="mr-merkmale">
                  <li>
                    {p.benutzer === 1 ? '1 Benutzer' : `Bis ${p.benutzer} Benutzer`}
                  </li>
                  <li>{p.speicherGb} GB für Belege und Fotos</li>
                  <li>{p.brandingText}</li>
                </ul>
                {!p.aktuell && darfBuchen('paket_wechseln') && (
                  <button
                    className="o-btn o-btn-primary"
                    id={`mr-btn-waehlen-${p.id}`}
                    disabled={laeuft !== null}
                    onClick={() => setWahl(wahl === p.id ? null : p.id)}
                  >
                    Dieses Paket wählen
                  </button>
                )}
                {wahl === p.id && (
                  <div className="mr-form" id={`mr-form-wechsel-${p.id}`}>
                    {/*
                      Zwei Wege, und der Unterschied ist nicht kosmetisch.

                      Ist Stripe eingerichtet, fuehrt der Weg ueber den
                      Checkout, und in Raporto aendert sich an dieser Stelle
                      NICHTS – das Paket wechselt erst, wenn Stripe die Zahlung
                      per Webhook bestaetigt hat. Deshalb steht hier auch kein
                      «Wechsel bestaetigen»: bestaetigt wird bei Stripe.

                      Ohne Stripe bleibt der bisherige Weg. Er ist der Stand
                      von heute und wird nicht entfernt, solange es Betriebe
                      gibt, die kein Abo bei Stripe haben.
                    */}
                    {z.eingerichtet ? (
                      <>
                        <p>
                          Du buchst <b>{p.name}</b>{' '}
                          {intervall === 'jahr' ? 'als Jahresabo' : 'monatlich'}. Das neue Paket
                          gilt, sobald die Zahlung bestätigt ist. Preise exkl. MWST; die
                          Mehrwertsteuer wird auf der Rechnung ausgewiesen.
                        </p>
                        <div className="mr-aktionen">
                          <button className="o-btn o-btn-ghost" onClick={() => setWahl(null)}>
                            Abbrechen
                          </button>
                          <button
                            className="o-btn o-btn-primary"
                            id={`mr-btn-checkout-${p.id}`}
                            disabled={laeuft !== null}
                            onClick={() =>
                              void zuStripe('checkout', () =>
                                api.aboCheckout(p.id, intervall, uuidv7())
                              )
                            }
                          >
                            {wartet('checkout') ? 'Einen Moment …' : 'Weiter zur Zahlung'}
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <p>
                          Dein Paket wechselt auf <b>{p.name}</b>. Es geht nichts verloren, und du
                          kannst später wieder wechseln.
                        </p>
                        <div className="mr-aktionen">
                          <button className="o-btn o-btn-ghost" onClick={() => setWahl(null)}>
                            Abbrechen
                          </button>
                          <button
                            className="o-btn o-btn-primary"
                            id={`mr-btn-wechsel-${p.id}`}
                            disabled={laeuft !== null}
                            onClick={() =>
                              void handeln('wechsel', async () => {
                                await api.aboPaket(p.id, uuidv7())
                                return `Paket gewechselt auf ${p.name}.`
                              })
                            }
                          >
                            {wartet('wechsel') ? 'Wird gewechselt …' : 'Wechsel bestätigen'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          {/*
            Der Plattformhinweis steht auf jeder Karte und nicht nur in den
            AGB. Wer erst nach dem Kauf erfährt, dass auf seiner Rechnung ein
            fremder Name steht, hat den Namen nicht gekauft, sondern
            hingenommen.
          */}
          <p className="o-soft" id="mr-branding">
            {stand.branding.sichtbar
              ? 'Auf deinen Rechnungen und Offerten steht unten rechts «Powered by Raporto · raporto.ch».'
              : 'Auf deinen Rechnungen und Offerten steht kein Hinweis auf Raporto.'}
            {stand.branding.addonBuchbar &&
              ` Mit dem White-Label-Add-on lässt er sich später für CHF ${preisText(
                stand.branding.addonPreisRp
              )}/Monat entfernen — das kommt zusammen mit der Abrechnung.`}
          </p>
        </div>
      </section>

      {/* ---------- Rechnungen ---------- */}
      <section className="o-card" id="mr-rechnungen">
        <div className="o-card-head">
          <h3>Rechnungen von Raporto</h3>
        </div>
        <div className="o-pad">
          {/*
            Eine leere Liste wäre hier die schlechtere Antwort: Sie sähe aus
            wie «noch keine Rechnung» und also wie «wird schon noch kommen».
            Der ehrliche Satz ist, dass es die Abrechnung noch nicht gibt.
          */}
          <p className="o-soft">
            Noch keine — wir verrechnen im Moment nicht. Sobald die Abrechnung läuft, findest du
            deine Rechnungen hier, und wir sagen es dir vorher.
          </p>
        </div>
      </section>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   Die Sprache des Ausstiegs
   ═══════════════════════════════════════════════════════════════════════════

   Kurz, und je Fall anders. Eine offene Zahlung ist keine Kündigung; wer
   «starte dein Abo neu» liest, obwohl bloss seine Karte abgelaufen ist, hält
   uns für unaufmerksam – und sucht die falsche Lösung. */

function ausstiegstitel(grund: Ausstieg['grund']): string {
  switch (grund) {
    case 'zahlung_offen':
      return 'Zahlung offen'
    case 'abo_beendet':
      return 'Abo endet'
    case 'test_abgelaufen':
      return 'Testphase'
  }
}

/**
 * Die Phase als Etikett – dieselben vier Wörter wie im Band.
 *
 * Vorher stand hier «Ausstiegsfenster» und «gesperrt». Beides beschrieb zwei
 * verschiedene Abschnitte mit einem Wort: Die Nachfrist ändert nichts an der
 * Nutzung, der Lesemodus schon, und «gesperrt» klang nach beidem.
 */
function lagetext(phase: Ausstieg['phase']): string {
  switch (phase) {
    case 'laeuft':
      return 'läuft noch'
    case 'nachfrist':
      return 'Nachfrist'
    case 'lesemodus':
      return 'Lesemodus'
    case 'geloescht':
      return 'Frist abgelaufen'
  }
}

/**
 * Der Satz zur Phase – und er sagt dasselbe wie das Band.
 *
 * Zwei Bildschirme, ein Ablauf: Wer im Band «Lesemodus» liest und hier
 * «gesperrt», hält eines von beiden für falsch. Gerechnet wird in keinem von
 * beiden; die Phase kommt vom Server.
 */
function ausstiegssatz(a: Ausstieg): string {
  switch (a.phase) {
    case 'laeuft':
      /*
        Dieselbe Unterscheidung wie im Band – und sie muss hier dieselbe sein.
        Zwei Bildschirme, ein Ablauf: Wer hier «danach braucht es ein
        Zahlungsmittel» liest, nachdem er gekündigt hat, hält die Kündigung
        für nicht angekommen.
      */
      if (a.verlaengertNicht) {
        return 'Bis zum Enddatum arbeitest du normal weiter. Danach endet dein Abo; abgebucht wird nichts mehr.'
      }
      return a.abbuchungAm !== null
        ? 'Bis zum Enddatum arbeitest du normal weiter. An diesem Tag buchen wir ab; vorher entstehen keine Kosten.'
        : 'Bis zum Enddatum arbeitest du normal weiter. Danach braucht es ein Zahlungsmittel.'
    case 'nachfrist':
      return a.grund === 'zahlung_offen'
        ? 'Du arbeitest unverändert weiter. Aktualisiere dein Zahlungsmittel über die Abo-Verwaltung – sobald eine Zahlung durchgeht, läuft alles weiter.'
        : 'Du arbeitest unverändert weiter. Danach wechselt der Zugang in den Lesemodus: ansehen und exportieren ja, neue Vorgänge nein.'
    case 'lesemodus':
      return 'Du siehst alle Daten und kannst sie exportieren; neue Rechnungen, Offerten und Rapporte entstehen keine. Hol die Zahlung nach, dann steht der Zugang sofort wieder offen.'
    case 'geloescht':
      return 'Die Frist ist abgelaufen und der Betrieb ist zur Löschung vorgesehen. Melde dich bei uns, wenn du deine Daten noch brauchst.'
  }
}
